import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Link2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppFieldGroup,
  AppInput,
  AppSectionHeader,
  AppText,
} from '../../../ui-foundation';
import { buildHouseholdSharingSummary } from '../../../ui-foundation/features/family';

function statusClass(status: 'invited' | 'active') {
  return status === 'active'
    ? 'bg-success-light text-success'
    : 'bg-plum-light text-primary';
}

export default function HouseholdInviteSection({
  surface = 'family',
}: {
  surface?: 'profile' | 'family';
}) {
  const navigate = useNavigate();
  const { data, user, refreshData } = useApp();
  const [creatingShare, setCreatingShare] = useState(false);
  const [joiningShare, setJoiningShare] = useState(false);
  const [joinCode, setJoinCode] = useState('');

  const householdShare = data.householdShare;
  const displayName = useMemo(
    () => data.profile.firstName.trim() || user?.email?.split('@')[0] || 'Household',
    [data.profile.firstName, user?.email],
  );
  const manageInProfile = surface === 'profile';
  const householdSummary = useMemo(
    () =>
      buildHouseholdSharingSummary({
        share: householdShare,
        currentUserId: user?.id || null,
        partnerName: data.profile.partnerName,
      }),
    [data.profile.partnerName, householdShare, user?.id],
  );

  const copyInviteCode = async () => {
    if (!householdShare?.inviteCode) return;
    await navigator.clipboard.writeText(householdShare.inviteCode);
    toast.success('Household code copied.');
  };

  const createShare = async () => {
    if (!user) return;

    setCreatingShare(true);
    const { error } = await supabase.rpc('create_household_share' as any, {
      owner_display_name: displayName,
    });
    setCreatingShare(false);

    if (error) {
      toast.error(error.message || 'Could not create the household code right now.');
      return;
    }

    await refreshData();
    toast.success('Household code ready.');
  };

  const joinShare = async () => {
    if (!user) return;
    if (!joinCode.trim()) {
      toast.error('Add the household code first.');
      return;
    }

    setJoiningShare(true);
    const { error } = await supabase.rpc('join_household_share' as any, {
      share_invite_code: joinCode.trim().toUpperCase(),
      joining_display_name: displayName,
    });
    setJoiningShare(false);

    if (error) {
      toast.error(error.message || 'Could not join that household code.');
      return;
    }

    setJoinCode('');
    await refreshData();
    toast.success('Household linked.');
  };

  return (
    <AppCard>
      <AppSectionHeader
        title="Partner / household invite"
        subtitle={
          manageInProfile
            ? 'Link one other adult here in My Settings so shared household tools are ready before you start using Family and Money.'
            : 'Household linking now lives in My Settings. Family uses the shared household trail after you link it there.'
        }
      />

      <div className="mt-4 space-y-3">
        {householdShare ? (
          <div className="rounded-2xl border border-border bg-background px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusClass(householdShare.status)}`}>
                {householdSummary.statusLabel}
              </span>
              <AppText fontSize="$sm" fontWeight="700">
                {householdSummary.headline}
              </AppText>
            </div>
            {householdSummary.ownershipLabel ? (
              <AppText className="mt-2" fontSize="$xs" color="$colorMuted">
                {householdSummary.ownershipLabel}
              </AppText>
            ) : null}
            <AppText className="mt-2" fontSize="$sm" color="$colorMuted">
              {householdSummary.body}
            </AppText>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div className="rounded-2xl border border-border bg-card px-4 py-3">
                <AppText fontSize="$xs" fontWeight="700">{householdSummary.sharedScopeTitle}</AppText>
                <div className="mt-2 space-y-1.5">
                  {householdSummary.sharedScopeItems.map((item) => (
                    <AppText key={item} fontSize="$xs" color="$colorMuted">
                      {`• ${item}`}
                    </AppText>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-border bg-card px-4 py-3">
                <AppText fontSize="$xs" fontWeight="700">{householdSummary.privateScopeTitle}</AppText>
                <div className="mt-2 space-y-1.5">
                  {householdSummary.privateScopeItems.map((item) => (
                    <AppText key={item} fontSize="$xs" color="$colorMuted">
                      {`• ${item}`}
                    </AppText>
                  ))}
                </div>
              </div>
            </div>
            <AppText className="mt-3" fontSize="$xs" color="$colorMuted">
              {householdSummary.nextStep}
            </AppText>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-border bg-card px-3 py-1.5 text-[11px] font-semibold tracking-[0.16em] text-foreground">
                {householdShare.inviteCode}
              </span>
              <AppButton size="sm" tone="outline" onPress={() => void copyInviteCode()}>
                <Copy size={12} />
                Copy code
              </AppButton>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-background px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">
                {householdSummary.statusLabel}
              </span>
              <AppText fontSize="$sm" fontWeight="700">
                {householdSummary.headline}
              </AppText>
            </div>
            <AppText className="mt-2" fontSize="$sm" color="$colorMuted">
              {manageInProfile
                ? householdSummary.body
                : 'If you want to create or join a household code, do that in My Settings first. Family will pick up the shared household trail once it is linked.'}
            </AppText>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div className="rounded-2xl border border-border bg-card px-4 py-3">
                <AppText fontSize="$xs" fontWeight="700">{householdSummary.sharedScopeTitle}</AppText>
                <div className="mt-2 space-y-1.5">
                  {householdSummary.sharedScopeItems.map((item) => (
                    <AppText key={item} fontSize="$xs" color="$colorMuted">
                      {`• ${item}`}
                    </AppText>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-border bg-card px-4 py-3">
                <AppText fontSize="$xs" fontWeight="700">{householdSummary.privateScopeTitle}</AppText>
                <div className="mt-2 space-y-1.5">
                  {householdSummary.privateScopeItems.map((item) => (
                    <AppText key={item} fontSize="$xs" color="$colorMuted">
                      {`• ${item}`}
                    </AppText>
                  ))}
                </div>
              </div>
            </div>
            <AppText className="mt-3" fontSize="$xs" color="$colorMuted">
              {householdSummary.nextStep}
            </AppText>
          </div>
        )}

        {!householdShare && manageInProfile ? (
          <AppButtonRow>
            <AppButton size="sm" onPress={() => void createShare()} disabled={creatingShare}>
              <Users size={12} />
              {creatingShare ? 'Creating…' : 'Create household code'}
            </AppButton>
          </AppButtonRow>
        ) : null}

        {!householdShare && manageInProfile ? (
          <div className="rounded-2xl border border-border bg-background px-4 py-3">
            <AppText fontSize="$sm" fontWeight="700">Join a household code</AppText>
            <AppText className="mt-1" fontSize="$sm" color="$colorMuted">
              Use the code from the other adult to link the shared household trail to this account.
            </AppText>
            <div className="mt-3">
              <AppFieldGroup label="Household code">
                <AppInput
                  tag="input"
                  value={joinCode}
                  onChange={(event) => setJoinCode((event.target as HTMLInputElement).value.toUpperCase())}
                  placeholder="AB12CD"
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                />
              </AppFieldGroup>
            </div>
            <AppButtonRow mt="$sm">
              <AppButton size="sm" tone="outline" onPress={() => void joinShare()} disabled={joiningShare}>
                <Link2 size={12} />
                {joiningShare ? 'Joining…' : 'Join household'}
              </AppButton>
            </AppButtonRow>
          </div>
        ) : null}

        {!manageInProfile ? (
          <AppButton tone="outline" size="sm" onPress={() => navigate('/profile')}>
            Open My Settings
          </AppButton>
        ) : null}
      </div>
    </AppCard>
  );
}
